import { Injectable } from '@angular/core';
import { FeatureToggleService } from './feature-toggle.service';
import { AdminService } from './admin.service';
import { FeatureFlags } from '../interfaces/feature-flags';
import { CloudLinkNotifyService } from './cloudlink-notify.service';
import { asyncJobOwner, asyncJobStatus, NotifyAlertType } from '../constants/constants';
import { AccountsService } from './accounts.service';
import { IntegZappaPhoneService } from './integ-zappa-phone.service';
import { AsyncJobsService } from './async-jobs.service';
import { BillingAsyncJobType, Tenant, TenantType  } from '../interfaces/tenants';
import { ExternalIntegrationActionType, ExternalIntegrationChange, StatusString } from '../interfaces/integrations';
import { ExternalRefreshNotifierService } from './external-refresh-notifier.service';
import { ExternalRefreshNotifierConfig, ExternalRefreshNotifierEmittedData } from '../interfaces/external-refresh-notifier';
import { TranslateService } from '@ngx-translate/core';
import LRUCache from 'lru-cache';
import { Util } from '../utils/Util';

const logContext = 'ZappaAccountService';

@Injectable({
    providedIn: 'root'
  })

export class ZappaAccountService {

  // These caches have been moved here because the zappa phone integration service is not 
  // easily accessible from external sources and this cache could be updated by external events 
  public completeSetupCache = new LRUCache<string, Promise<boolean | undefined>>({
		ttl: 60 * 5 * 1000,  // ms (cache item expires in 5 min)
		max: 5          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID

	public statusMessageCache = new LRUCache<string, Promise<StatusString | undefined>>({
		ttl: 60 * 1 * 1000,  // ms (cache item expires in 1 min)
		max: 5          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID

  // store tenant for account if exists
  public tenantCache = new LRUCache<string, Tenant>({
    ttl: 60 * 2 * 1000,
    max: 5
  }); // cache by account ID


  constructor(
    private featureToggleService: FeatureToggleService,
    private notifyService: CloudLinkNotifyService,
    private accountsService: AccountsService,
    private zappaIntegService: IntegZappaPhoneService,
    private asyncJobsService: AsyncJobsService,
    private refreshService: ExternalRefreshNotifierService,
    public translateService: TranslateService,
    private adminService: AdminService) {

    // Zappa report generation and async delete notifications of completion 
    // will arrive here
    this.notifyService.eventNotification$.subscribe(async event => {
      console.log(`${logContext}.eventNotification$ : content after get `, event);      
      // backward compatibility when there is no clientJobTag it's a Zoom event
      if ( event.topic == 'platform-api-billing' && (!event.content?.clientJobTag?.tenantType || event.content?.clientJobTag?.tenantType == TenantType.ZOOM)){
        this.handleBillingAsyncEvent(event);
      } else {
        console.log(`${logContext}  eventNotification$ ignored - not a Zoom billing async event`);
      }
    });
  }

  initializeAsyncEventMonitor() {
    this.notifyService.initializeNotifyForBillingAsync();
  }


  async handleBillingAsyncEvent(event)
  {
    console.log(`${logContext}.handleBillingAsyncEvent event=`, event);
    
    if (!event?.content)
      return; // nothing that we can do

    if (event?.content?.acknowledged == true)
      return; // do not react to the acknowledged event

    // update the notification list first - does not matter the job type
    // if the job was already acknowledged, then nothing to update
    let jobUpdate = {
      jobType: event.content.jobType,
      jobId: event.content.jobId,
      status: event.content.status,
      owner: asyncJobOwner.ZAPPA_PHONE,
      createdOn: new Date(event.content.createdOn),
      modifiedOn: new Date(event.content.modifiedOn),
      clientJobTag: event.content.clientJobTag
    }
    this.asyncJobsService.handleTenantAsyncJobChangeEvent(jobUpdate);

    // rest is async delete handling
    if (event.content.jobType == BillingAsyncJobType.TENANT_DELETION)
    {
      let account = this.accountsService.getCurrentAccount();
      // there has been a change to the status of the delete operation
      // if it is complete, just complete the delete integration.
      if ( event.content.status == asyncJobStatus.COMPLETE )
      {
        await this.zappaIntegService.asyncJobIsComplete(account, event.content.jobId);
        let change: ExternalIntegrationChange = {
          action: ExternalIntegrationActionType.DELETE,
          productCode: 'Zappa'
        }

        let emittedData: ExternalRefreshNotifierEmittedData = {
          featureId: 'IntegrationChange',
          data: change
        }

        let config: ExternalRefreshNotifierConfig = {
          message: 'integrations.zappa.delete.success',
          actionString: 'integrations.zappa.delete.refreshLink',
          type: NotifyAlertType.SUCCESS,
          emittedData: emittedData
        }
        this.refreshService.externalSourceRequestsRefresh(config);
      }
      else if ( event?.content.status == asyncJobStatus.FAILED )
      {
        let retMsg = Util.extractMessageFromError(event.content.results); // I think for now, just extract the message

        this.zappaIntegService.asyncJobFailed(account, event.content.jobId, retMsg || event.content.results);
        let change: ExternalIntegrationChange = {
          action: ExternalIntegrationActionType.UPDATE,
          productCode: 'Zappa'
        }

        let emittedData: ExternalRefreshNotifierEmittedData = {
          featureId: 'IntegrationChange',
          data: change
        }

        let config: ExternalRefreshNotifierConfig = {
          message: this.translateService.instant('integrations.zappa.delete.failed') + (retMsg ? ' ' + retMsg : ''),
          actionString: 'integrations.zappa.delete.refreshLink',
          type: NotifyAlertType.DANGER,
          emittedData: emittedData
        }
        this.refreshService.externalSourceRequestsRefresh(config);
      }
    }
  }

  doesAccountHaveZappaPhone(accountId: string): Promise<boolean>
  {
    // begin remove when FF removed
    let zappaFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_ZAPPA_PHONE);
    if (!zappaFlag)
      return Promise.resolve(false);
    // end remove when FF removed
    
    return this.adminService.getAccountTags( accountId ).then( tags => {
      if ( tags && tags.Zappa && tags.Zappa.setupComplete == true )
      {
          return true;
      }
      return false;
    })
    .catch(err => {
      console.error(`${logContext} getAccountTags failed`, err);
      return false;
    });
  }
}
  